import React, { type FC, useCallback, type ReactNode } from 'react';

import { styled } from '@compiled/react';
import {
	graphql,
	useFragment,
	usePaginationFragment,
	useRelayEnvironment,
	fetchQuery,
} from 'react-relay';
import Button, { LoadingButton } from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagsService } from '@atlassian/jira-flags';

import { useIntl } from '@atlassian/jira-intl';
import { useRightSidebarController } from '@atlassian/jira-layout-controller';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	DRAWER,
} from '@atlassian/jira-product-analytics-bridge';
import type { content_servicedeskSettingsVirtualAgentCreateIntents_PaginatedIntentTemplatesQuery } from '@atlassian/jira-relay/src/__generated__/content_servicedeskSettingsVirtualAgentCreateIntents_PaginatedIntentTemplatesQuery.graphql';
import type { content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_QuestionsQuery } from '@atlassian/jira-relay/src/__generated__/content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_QuestionsQuery.graphql';
import type { content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_intentTemplatesDataRef$key } from '@atlassian/jira-relay/src/__generated__/content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_intentTemplatesDataRef.graphql';
import type { content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_virtualAgentFeaturesRef$key } from '@atlassian/jira-relay/src/__generated__/content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_virtualAgentFeaturesRef.graphql';
import { useIntentTemplateStoreActions } from '../../controllers/intent-template-store';
import { ErrorDisplay } from './error-display';
import { IntentTemplate } from './intent-template';
import { LastUpdatedTimestamp } from './last-updated-timestamp';
import messages from './messages';

const PAGINATION_COUNT = 20;

export const SidebarContent: FC<{
	intentTemplatesDataRef: content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_intentTemplatesDataRef$key;
	virtualAgentFeaturesRef: content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_virtualAgentFeaturesRef$key;
}> = ({ intentTemplatesDataRef, virtualAgentFeaturesRef }) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();
	const { closeRightSidebar } = useRightSidebarController();
	const { setQuestions, clearQuestions, setCancelQuestionsRequest } =
		useIntentTemplateStoreActions();
	const relayEnvironment = useRelayEnvironment();

	const adminHubData = useFragment(
		graphql`
			fragment content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_virtualAgentFeaturesRef on VirtualAgentFeatures
			@argumentDefinitions(isGateIntentTemplatesBehindAiToggleEnabled: { type: "Boolean!" }) {
				isAiEnabledInAdminHub @include(if: $isGateIntentTemplatesBehindAiToggleEnabled)
			}
		`,
		virtualAgentFeaturesRef,
	);

	const handleFetchQuestionsError = useCallback(
		(error?: Error) => {
			showFlag({
				title: formatMessage(messages.errorFlagTitleQuestions),
				description: formatMessage(messages.errorFlagDescriptionQuestions),
				type: 'error',
			});
			// https://data-portal.internal.atlassian.com/analytics/registry/55493
			fireErrorAnalytics({
				meta: {
					id: 'fetchIntentTemplateQuestions',
					teamName: 'jsd-flux-capacitors',
					packageName: 'jiraServicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar',
				},
				error,
			});
		},
		[formatMessage, showFlag],
	);

	const fetchQuestions = useCallback(
		(templateId: string) => {
			fetchQuery<content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_QuestionsQuery>(
				relayEnvironment,
				graphql`
					query content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_QuestionsQuery(
						$id: ID!
					) {
						node(id: $id) @required(action: THROW) {
							__typename
							... on VirtualAgentIntentTemplate {
								questions @required(action: THROW)
							}
						}
					}
				`,
				{
					id: templateId,
				},
			).subscribe({
				start(subscription) {
					setCancelQuestionsRequest(() => subscription.unsubscribe());
					clearQuestions();
				},
				next(data) {
					if (data && data.node.__typename === 'VirtualAgentIntentTemplate') {
						setQuestions(data.node.questions);
					} else {
						handleFetchQuestionsError();
					}
				},
				error(err: Error) {
					setCancelQuestionsRequest(null);
					handleFetchQuestionsError(err);
				},
				complete() {
					setCancelQuestionsRequest(null);
				},
			});
		},
		[
			relayEnvironment,
			setCancelQuestionsRequest,
			clearQuestions,
			setQuestions,
			handleFetchQuestionsError,
		],
	);

	const onCloseButtonClick = () => {
		closeRightSidebar('intentTemplate');
	};

	const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
		content_servicedeskSettingsVirtualAgentCreateIntents_PaginatedIntentTemplatesQuery,
		content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_intentTemplatesDataRef$key
	>(
		graphql`
			fragment content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_intentTemplatesDataRef on JiraProject
			@argumentDefinitions(
				count: { type: "Int", defaultValue: 20 }
				cursor: { type: "String" }
				isDiscoveredTemplatesTimestampEnabled: { type: "Boolean!" }
			)
			@refetchable(
				queryName: "content_servicedeskSettingsVirtualAgentCreateIntents_PaginatedIntentTemplatesQuery"
			) {
				intentTemplates(first: $count, after: $cursor)
					@optIn(to: "VirtualAgentIntentTemplate")
					@connection(
						key: "content_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar_SidebarContent_intentTemplates"
					) {
					hasDiscoveredTemplates @include(if: $isGateIntentTemplatesBehindAiToggleEnabled)
					...lastUpdatedTimestamp_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar
						@arguments(
							isDiscoveredTemplatesTimestampEnabled: $isDiscoveredTemplatesTimestampEnabled
						)
					edges {
						node {
							id
							...intentTemplate_servicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar
						}
					}
				}
			}
		`,
		intentTemplatesDataRef,
	);

	const loadMoreTemplates = () => {
		loadNext(PAGINATION_COUNT, {
			onComplete: (requestError) => {
				if (requestError) {
					showFlag({
						title: messages.errorFlagTitle,
						description: messages.errorFlagDescription,
						type: 'error',
					});
					// https://data-portal.internal.atlassian.com/analytics/registry/53592
					fireErrorAnalytics({
						meta: {
							id: 'loadMoreIntentTemplates',
							teamName: 'jsd-flux-capacitors',
							packageName: 'jiraServicedeskSettingsVirtualAgentCreateIntentTemplatesSidebar',
						},
						error: requestError,
					});
				} else {
					// https://data-portal.internal.atlassian.com/analytics/registry/53593
					fireOperationalAnalytics(createAnalyticsEvent({}), 'intentsTemplates loaded');
				}
			},
		});
	};

	const analyticsAttributes = {
		discoveredTemplatesDisplayed: data.intentTemplates?.hasDiscoveredTemplates,
	};

	return data == null || data.intentTemplates == null ? (
		<ErrorDisplay refetch={() => refetch({})} />
	) : (
		<ContextualAnalyticsData
			sourceType={DRAWER}
			sourceName="servicedeskVirtualAgentCreateIntentTemplatesSidebar"
			attributes={analyticsAttributes}
		>
			<SidebarContentContainer>
				<HeadingContainer>
					<Heading size="medium">{formatMessage(messages.panelHeading)}</Heading>
					<Button
						aria-label={formatMessage(messages.closeIconLabel)}
						appearance="subtle"
						spacing="none"
						onClick={onCloseButtonClick}
						iconAfter={<EditorCloseIcon label="" />}
					/>
				</HeadingContainer>
				<p>{formatMessage(messages.panelDescription)}</p>
				{ff('activate-ai-toggle-message-in-intent-templates-sidebar_9dhqx') &&
					!adminHubData.isAiEnabledInAdminHub && (
						<SectionMessage title={formatMessage(messages.activateAiSectionMessageTitle)}>
							<p>
								{formatMessage(messages.activateAiSectionMessageDescription, {
									link: (...chunks: ReactNode[]) => (
										<Button
											appearance="link"
											spacing="none"
											href="https://support.atlassian.com/organization-administration/docs/activate-atlassian-intelligence-for-products/"
											rel="noopener noreferrer"
											target="_blank"
										>
											{chunks}
										</Button>
									),
								})}
							</p>
						</SectionMessage>
					)}
				{ff('activate-ai-toggle-message-in-intent-templates-sidebar_9dhqx') &&
					adminHubData.isAiEnabledInAdminHub &&
					data.intentTemplates.hasDiscoveredTemplates === false && (
						<SectionMessage title={formatMessage(messages.loadingTemplatesSectionMessageTitle)}>
							<p>
								{formatMessage(messages.loadingTemplatesSectionMessageDescriptionOne, {
									b: (...parts: ReactNode[]) => <strong>{parts}</strong>,
								})}
							</p>
							<p>
								{formatMessage(messages.loadingTemplatesSectionMessageDescriptionTwo, {
									link: (...chunks: ReactNode[]) => (
										<Button
											appearance="link"
											spacing="none"
											href="https://support.atlassian.com/contact/#/"
											rel="noopener noreferrer"
											target="_blank"
										>
											{chunks}
										</Button>
									),
								})}
							</p>
						</SectionMessage>
					)}
				{ff('add-last-updated-timestamp-for-intent-templates_tgonx') && (
					<LastUpdatedTimestamp intentTemplatesDataRef={data.intentTemplates} />
				)}
				{data?.intentTemplates?.edges && data?.intentTemplates?.edges.length > 0 && (
					<IntentTemplatesContainer>
						{data.intentTemplates.edges.map(
							(edge) =>
								edge.node && (
									<IntentTemplate
										key={edge.node.id}
										intentTemplateDataRef={edge.node}
										onSelect={() => edge.node && fetchQuestions(edge.node.id)}
									/>
								),
						)}
					</IntentTemplatesContainer>
				)}
				{hasNext && (
					<ButtonContainer>
						<LoadingButton isLoading={isLoadingNext} onClick={loadMoreTemplates}>
							{formatMessage(messages.loadMoreTemplatesButton)}
						</LoadingButton>
					</ButtonContainer>
				)}
			</SidebarContentContainer>
			{/* https://data-portal.internal.atlassian.com/analytics/registry/61365 */}
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SidebarContentContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	justifyContent: 'space-between',
	paddingLeft: token('space.300', '24px'),
	paddingRight: token('space.300', '24px'),
	paddingTop: token('space.300', '24px'),
	boxSizing: 'border-box',
	rowGap: `${token('space.200', '16px')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IntentTemplatesContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	padding: `0 0 ${token('space.200', '16px')} 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'space-between',
});
